import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router";

/**
 * Scroll behavior for the app shell. The app scrolls #root instead of the
 * document (theme.css locks body scroll to keep the mobile browser chrome and
 * bottom tab bar stable), which has two consequences this hook repairs:
 *
 * 1. The browser's own scroll restoration only watches the window, so #root
 *    kept its offset across route changes — new screens opened mid-scroll.
 *    Native behavior: forward navigations start at the top, back/forward
 *    returns to where you were.
 *
 * 2. When the on-screen keyboard opens, iOS pans the *window* up to reveal
 *    the focused input. With document scroll locked the user can never pan
 *    it back, so after the keyboard closed the app could be left permanently
 *    shifted — fixed elements (tab bar) stranded away from the screen edge.
 *    Whenever the keyboard closes, snap the leftover pan back to 0.
 */
export function useScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();
  const positions = useRef(new Map<string, number>());
  const keyRef = useRef(location.key);

  // Track #root's scroll continuously under the current history entry's key.
  // (Reading scrollTop at navigation time is too late — by then the new
  // page's shorter content may have clamped it.)
  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;
    const onScroll = () => {
      positions.current.set(keyRef.current, root.scrollTop);
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => root.removeEventListener("scroll", onScroll);
  }, []);

  // On navigation: restore for back/forward, top for push/replace. Layout
  // effect so it lands before paint — no flash of the old position.
  useLayoutEffect(() => {
    keyRef.current = location.key;
    const root = document.getElementById("root");
    if (!root) return;
    root.scrollTop = navType === "POP" ? (positions.current.get(location.key) ?? 0) : 0;
  }, [location.key, navType]);

  // Undo any window pan iOS leaves behind after the keyboard closes.
  useEffect(() => {
    const vv = window.visualViewport;
    const snapBack = () => {
      const scroller = document.scrollingElement ?? document.documentElement;
      if (window.scrollY > 0 || scroller.scrollTop > 0) {
        window.scrollTo(0, 0);
        scroller.scrollTop = 0;
      }
    };
    const onResize = () => {
      // Viewport back to (roughly) full height ⇒ keyboard just closed.
      if (!vv || vv.height >= window.innerHeight - 60) snapBack();
    };
    const onFocusOut = () => {
      // The pan settles a beat after blur; check once it has.
      setTimeout(onResize, 250);
    };
    snapBack();
    vv?.addEventListener("resize", onResize);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      vv?.removeEventListener("resize", onResize);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
}
